import React from 'react';
import {
  IFarmInputPurchaseOrder,
  IGenericType,
  IRouteScreenProps,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  apiGetPurchaseOrders,
  apiGetPurchaseOrderStatuses,
  apiGetPurchaseOrdersByInvoice,
} from 'constants/Api';
import {
  Box,
  Button,
  ButtonGroup,
  Snackbar,
  Typography,
} from '@material-ui/core';
import {connect} from 'react-redux';
import {Route} from 'react-router-dom';
import NavigationService from 'services/NavigationService';
import {KhulaSupplierTable, Row} from 'components/Table/KhulaSupplierTable';
import moment from 'moment';
import {
  reduxSetPurchaseOrders,
  reduxSetPurchaseOrderStatuses,
} from 'appredux/actions';
import {PurchaseOrderState} from 'appredux/reducers/reducer-purchase-orders';
import {
  purchaseOrderTableFilters,
  purchaseOrderTableHeaders,
  purchaseOrderTableRows,
} from 'screens/Dashboard/PurchaseOrders/PurchaseOrderTableSetup';
import {PurchaseOrderStatusesState} from 'appredux/reducers/reducer-purchase-order-statuses';

type PurchaseOrderProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
  purchaseOrderState: PurchaseOrderState;
  purchaseOrderStatusesState: PurchaseOrderStatusesState;
};

class PurchaseOrders extends React.Component<PurchaseOrderProps> {
  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    fetchSize: number;
    filteredPurchaseOrders: IFarmInputPurchaseOrder[] | undefined;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    fetchSize: 20,
    filteredPurchaseOrders: undefined,
  };

  componentDidMount() {
    const invoiceId = new URLSearchParams(this.props.location?.search).get(
      'invoiceId',
    );

    if (invoiceId) {
      this.setState({isLoading: true}, () => {
        apiGetPurchaseOrdersByInvoice(Number(invoiceId))
          .then((pos) => this.setState({filteredPurchaseOrders: pos}))
          .catch((err) => {
            alert('Error loading purchase orders. Please try again later');
            console.error(err);
          })
          .finally(() => this.setState({isLoading: false}));
      });
    } else {
      const {purchaseOrderState} = this.props;
      const noInvoices = !purchaseOrderState.purchaseOrders?.length;
      this.setState({isLoading: noInvoices}, () => {
        this.loadPurchaseOrders()
          .then(() => this.loadPurchaseOrderStatuses())
          .catch((err) => {
            alert('Error loading purchase orders. Please try again later');
            console.error(err);
          })
          .finally(() => this.setState({isLoading: false}));
      });
    }
  }

  refreshPurchaseOrders() {
    this.setState({isRefreshing: true}, () => {
      apiGetPurchaseOrders(this.state.fetchSize)
        .then((purchaseOrders: IFarmInputPurchaseOrder[]) => {
          reduxSetPurchaseOrders(purchaseOrders);
          this.setState({showRefreshedSnackbar: true});
        })
        .then(() => apiGetPurchaseOrderStatuses())
        .then((purchaseOrderStatuses) =>
          reduxSetPurchaseOrderStatuses(purchaseOrderStatuses),
        )
        .catch((err) => {
          alert('Failed to load purchase orders. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false, isRefreshing: false}));
    });
  }

  loadPurchaseOrders() {
    const {purchaseOrderState} = this.props;
    const {lastUpdate, purchaseOrders} = purchaseOrderState;

    const invoicesAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(30, 'minutes'),
    );
    const noInvoices = !purchaseOrders?.length;

    if (invoicesAreExpired || noInvoices)
      return apiGetPurchaseOrders(this.state.fetchSize).then(
        (purchaseOrders: IFarmInputPurchaseOrder[]) => {
          reduxSetPurchaseOrders(purchaseOrders);
        },
      );

    return Promise.resolve();
  }

  loadPurchaseOrderStatuses() {
    const {purchaseOrderStatusesState} = this.props;
    const {lastUpdate, purchaseOrderStatuses} = purchaseOrderStatusesState;

    const isExpired = moment(lastUpdate).isBefore(
      moment().subtract(24, 'hours'),
    );
    const noStatuses = !purchaseOrderStatuses?.length;

    if (isExpired || noStatuses)
      return apiGetPurchaseOrderStatuses()
        .then((statuses) => reduxSetPurchaseOrderStatuses(statuses))
        .then(() => {});

    return Promise.resolve();
  }

  handleFetchSizeUpdate(size?: number) {
    this.setState({fetchSize: size}, () => {
      this.refreshPurchaseOrders();
    });
  }

  render() {
    const {purchaseOrderState, purchaseOrderStatusesState} = this.props;
    const {lastUpdate, purchaseOrders: allPurchaseOrders} = purchaseOrderState;
    const {purchaseOrderStatuses} = purchaseOrderStatusesState;
    const {
      isLoading,
      isRefreshing,
      showRefreshedSnackbar,
      fetchSize,
      filteredPurchaseOrders,
    } = this.state;
    const purchaseOrders = filteredPurchaseOrders ?? allPurchaseOrders;

    return (
      <>
        <div className="table-screen orders-container">
          <Typography variant="h5" className="page-title">
            Purchase Orders
          </Typography>
          <Route
            render={({history}) => (
              <div className="orders-section">
                <Box className="actions-row">
                  <div style={{flex: 1}} />
                  {!filteredPurchaseOrders && (
                    <ButtonGroup variant="text">
                      {[20, 50, undefined].map((size, index) => (
                        <Button
                          key={index}
                          variant={fetchSize === size ? 'contained' : 'text'}
                          disabled={isLoading || isRefreshing}
                          disableElevation
                          onClick={() => this.handleFetchSizeUpdate(size)}
                          size={'small'}
                        >
                          {size || 'All'}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                </Box>
                <PurchaseOrdersTable
                  purchaseOrders={purchaseOrders}
                  purchaseOrderStatuses={purchaseOrderStatuses}
                  isLoading={isLoading}
                  isRefreshing={isRefreshing}
                  onRefresh={() => this.refreshPurchaseOrders()}
                />
                <Box mt={1}>
                  <Typography variant={'caption'}>
                    Last updated: {moment(lastUpdate).fromNow()}
                  </Typography>
                </Box>
                <Snackbar
                  anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                  open={showRefreshedSnackbar}
                  onClose={() => this.setState({showRefreshedSnackbar: false})}
                  autoHideDuration={3000}
                  message="Purchase orders refreshed"
                />
              </div>
            )}
          />
        </div>
      </>
    );
  }
}

function PurchaseOrdersTable(props: {
  purchaseOrders: IFarmInputPurchaseOrder[];
  purchaseOrderStatuses: IGenericType[];
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: VoidFunction;
}) {
  const {
    purchaseOrders,
    purchaseOrderStatuses,
    isLoading,
    isRefreshing,
    onRefresh,
  } = props;

  const mapRowToPurchaseOrder = (row: Row) =>
    // @ts-ignore
    purchaseOrders.find((invoice) => invoice.id == row.id);

  return (
    <KhulaSupplierTable
      headers={purchaseOrderTableHeaders()}
      rows={purchaseOrderTableRows(
        purchaseOrders,
        purchaseOrderStatuses,
        (purchaseOrder) => {
          NavigationService.navigate(`/purchase-orders/${purchaseOrder.id}`);
        },
      )}
      filtersGroups={purchaseOrderTableFilters}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      onRowSelect={(row) => {
        const purchaseOrder = mapRowToPurchaseOrder(row);
        if (!purchaseOrder) return;
        NavigationService.navigate(`/purchase-orders/${purchaseOrder.id}`);
      }}
    />
  );
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    purchaseOrderState: state.purchaseOrders,
    purchaseOrderStatusesState: state.purchaseOrderStatuses,
  };
};
export default connect(mapStateToProps)(PurchaseOrders);
